// SurveyDetailPage.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, Spin, message, Button } from "antd";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import SurveyMapView from "./SurveyMapView";
import { useJsApiLoader } from "@react-google-maps/api";

const SurveyDetailPage = () => {
  const { id } = useParams();
  const axiosInstance = useAxiosInstance();

  const [survey, setSurvey] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [polygonPaths, setPolygonPaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);


  const mainMapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Build polygon paths from shape data
  const buildPolygonPaths = (shapeList = []) => {
    const allPolygons = [];

    shapeList.forEach((shape) => {
      const { boundaries, name } = shape || {};
      if (!boundaries?.type || !boundaries?.coordinates) return;

      if (boundaries.type === "MultiPolygon") {
        boundaries.coordinates.forEach((poly) => {
          const ring = poly[0] || [];
          const coordinates = ring
            .map(([lng, lat]) => {
              const latNum = Number(lat);
              const lngNum = Number(lng);
              return Number.isNaN(latNum) || Number.isNaN(lngNum)
                ? null
                : { lat: latNum, lng: lngNum };
            })
            .filter(Boolean);

          if (coordinates.length)
            allPolygons.push({ name, coordinate: coordinates });
        });
      }

      if (boundaries.type === "Polygon") {
        boundaries.coordinates.forEach((ring) => {
          const coordinates = ring
            .map(([lng, lat]) => {
              const latNum = Number(lat);
              const lngNum = Number(lng);
              return Number.isNaN(latNum) || Number.isNaN(lngNum)
                ? null
                : { lat: latNum, lng: lngNum };
            })
            .filter(Boolean);

          if (coordinates.length)
            allPolygons.push({ name, coordinate: coordinates });
        });
      }
    });

    return allPolygons;
  };
  const fetchSurveyDetail = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(`api/surveyAnalytics/surveys/${id}`);
      const surveyData = res.data?.data;
      setSurvey(surveyData);

      if (surveyData?.isShapeExist) {
        try {
          const shapeRes = await axiosInstance.get(
            `/api/surveyAnalytics/getShapeFile/${id}`
          );
          const rawShapes = shapeRes.data?.data || [];
          console.log("rawShapes",rawShapes)
          setShapes(rawShapes);

          const polys = buildPolygonPaths(rawShapes);
          setPolygonPaths(polys);
        } catch (shapeErr) {
          console.error("Failed to load shapes", shapeErr);
          setShapes([]);
          setPolygonPaths([]);
        }
      } else {
        setShapes([]);
        setPolygonPaths([]);
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to load survey detail");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSurveyDetail();
  }, [id]);

  const defaultCenter = useMemo(() => {
    if (polygonPaths[0]?.coordinate?.length)
      return polygonPaths[0].coordinate[0];

    if (markers[0]) return { lat: markers[0].lat, lng: markers[0].lng };

    // Fallback: Lucknow
    return { lat: 26.8467, lng: 80.9462 };
  }, [polygonPaths, markers]);

  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <p>Failed to load map. Please check your API key or network.</p>
      </div>
    );
  }

  if (loading || !survey || !isLoaded) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card title={survey.surveyName} style={{ marginBottom: 24 }}>
        <p>
          <strong>Description:</strong> {survey.description}
        </p>
        <p>
          <strong>Questions:</strong> {survey.questionIds?.length || 0}
        </p>
        <p>
          <strong>Clients: </strong>
          {Array.isArray(survey.client)
            ? survey.client
                .map((c) =>
                  `${c.adminFirstName || ""} ${c.adminLastName || ""}`.trim()
                )
                .join(", ")
            : ""}
        </p>
      </Card>

      <Card
        title="Survey Responses Map"
        extra={
          <Button
            type="primary"
            className="bg-[#004877]"
            onClick={() => setIsFullScreen(true)}
          >
            Make Survey Dashboard
          </Button>
        }
      >
        <div
  className={`relative transition-all duration-300 ${
    isFullScreen
      ? "fixed inset-0 z-[9999] bg-black/80 p-4"
      : ""
  }`}
>


  <div className={`${isFullScreen ? "h-full" : "h-[500px]"} w-full`}>
    <SurveyMapView
      isLoaded={isLoaded}
      open={isFullScreen}     
      setIsFullScreen={setIsFullScreen}  
      id={id} 
      center={defaultCenter}
      markers={markers}
      polygonPaths={polygonPaths}
      mapRef={mainMapRef}
      survey={survey}
    />
  </div>
</div>
      </Card>
    </div>
  );
};

export default SurveyDetailPage;
